import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Lightbulb, Check, Users, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { BudgetPrint } from "./PrintViews";

const CATEGORIE = [
  "Location",
  "Catering",
  "Fotografo",
  "Video",
  "Musica/DJ",
  "Fiori e addobbi",
  "Abiti e accessori",
  "Trucco e parrucco",
  "Inviti e stampa",
  "Bomboniere",
  "Torta e dolci",
  "Trasporti",
  "Alloggio invitati",
  "Documenti e permessi",
  "Luna di miele",
  "Altro",
];

const STATI = {
  da_pianificare: "Da pianificare",
  prenotato: "Prenotato",
  acconto_pagato: "Acconto pagato",
  saldato: "Saldato",
};

const STATO_COLORS = {
  da_pianificare: "#9a9a9a",
  prenotato: "#5B6B8C",
  acconto_pagato: "#BFA588",
  saldato: "#5c8a5c",
};

// A starter checklist of the things that come up in almost every real Italian
// wedding, grouped by category -- lets the couple seed their list with one click
// instead of staring at an empty tracker.
const SUGGESTIONS = [
  { categoria: "Location", nome: "Sopralluogo e firma contratto location" },
  { categoria: "Location", nome: "Affitto struttura" },
  { categoria: "Location", nome: "Sala vestizioni" },
  { categoria: "Catering", nome: "Degustazione menu e conferma numero invitati" },
  { categoria: "Catering", nome: "Aperitivo di benvenuto" },
  { categoria: "Catering", nome: "Menù pranzo/cena" },
  { categoria: "Catering", nome: "Abbinamento vini" },
  { categoria: "Catering", nome: "Servizio torta" },
  { categoria: "Catering", nome: "Buffet della sera" },
  { categoria: "Catering", nome: "Bar/open bar serale" },
  { categoria: "Catering", nome: "Menù bambini" },
  { categoria: "Fotografo", nome: "Prenotare fotografo" },
  { categoria: "Video", nome: "Prenotare videomaker" },
  { categoria: "Video", nome: "Photo booth" },
  { categoria: "Musica/DJ", nome: "Prenotare DJ o band" },
  { categoria: "Musica/DJ", nome: "Noleggio impianto audio cerimonia" },
  { categoria: "Fiori e addobbi", nome: "Scegliere fiorista e addobbi tavoli" },
  { categoria: "Fiori e addobbi", nome: "Centrotavola" },
  { categoria: "Fiori e addobbi", nome: "Bouquet e bottoniere" },
  { categoria: "Fiori e addobbi", nome: "Tableau de Mariage" },
  { categoria: "Abiti e accessori", nome: "Abito sposa" },
  { categoria: "Abiti e accessori", nome: "Abito sposo" },
  { categoria: "Trucco e parrucco", nome: "Prova trucco e parrucco" },
  { categoria: "Inviti e stampa", nome: "Progettazione e stampa partecipazioni" },
  { categoria: "Inviti e stampa", nome: "Stampare e spedire inviti" },
  { categoria: "Bomboniere", nome: "Ordinare bomboniere" },
  { categoria: "Torta e dolci", nome: "Prenotare torta nuziale" },
  { categoria: "Trasporti", nome: "Auto/noleggio per gli sposi" },
  { categoria: "Alloggio invitati", nome: "Bloccare camere in hotel convenzionati" },
  { categoria: "Documenti e permessi", nome: "Pratiche comunali / documenti per il rito" },
  { categoria: "Documenti e permessi", nome: "Pubblicazioni di matrimonio" },
  { categoria: "Documenti e permessi", nome: "Diritti SIAE" },
  { categoria: "Altro", nome: "Wedding planner" },
  { categoria: "Luna di miele", nome: "Prenotare viaggio di nozze" },
];

function emptyForm() {
  return {
    categoria: CATEGORIE[0],
    nome: "",
    fornitore: "",
    costo_tipo: "fisso",
    costo_previsto: "",
    prezzo_unitario: "",
    moment_ref: "all",
    costo_effettivo: "",
    stato: "da_pianificare",
    scadenza: "",
    note: "",
    fatto: false,
  };
}

function fmtMoney(n) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}

export default function BudgetTab({ wedding, setWedding }) {
  const [items, setItems] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modalItem, setModalItem] = useState(null); // {...form} or null; has `.id` when editing
  const [showPrint, setShowPrint] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");

  const load = () => api.get("/owner/budget").then((r) => { setItems(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/owner/guests").then((r) => setGuests(r.data)).catch(() => {}); }, []);
  useEffect(() => { setBudgetInput(wedding.budget_totale != null ? String(wedding.budget_totale) : ""); }, [wedding.budget_totale]);

  const moments = useMemo(() => wedding.moments || [], [wedding.moments]);

  // "all" = every confirmed guest regardless of which moments they attend (the
  // right basis for a whole-day cost); a moment id narrows it to just the guests
  // attending that moment (e.g. only those at Pranzo), each counted as 1 + their
  // "ospiti extra" plus-ones -- the same headcount logic the table designer uses.
  const headcounts = useMemo(() => {
    const confirmed = guests.filter((g) => g.stato_rsvp === "confermato");
    const countOf = (list) => list.reduce((s, g) => s + 1 + (g.num_ospiti_extra || 0), 0);
    const map = { all: countOf(confirmed) };
    moments.forEach((m) => {
      map[m.id] = countOf(confirmed.filter((g) => g.moments?.includes(m.id)));
    });
    return map;
  }, [guests, moments]);

  const momentLabel = (momentRef) => (momentRef === "all" ? "Tutti i confermati (giornata intera)" : moments.find((m) => m.id === momentRef)?.label || "—");

  const totals = useMemo(() => {
    const previsto = items.reduce((s, i) => s + (i.costo_previsto || 0), 0);
    const pagato = items.reduce((s, i) => s + (i.costo_effettivo || 0), 0);
    const budgetTotale = wedding.budget_totale != null ? Number(wedding.budget_totale) : null;
    const rimanente = budgetTotale != null ? budgetTotale - pagato : null;
    const pct = budgetTotale ? Math.min(100, Math.round((pagato / budgetTotale) * 100)) : null;
    return { previsto, pagato, budgetTotale, rimanente, pct };
  }, [items, wedding.budget_totale]);

  const grouped = useMemo(() => {
    const byCat = new Map();
    items.forEach((it) => {
      if (!byCat.has(it.categoria)) byCat.set(it.categoria, []);
      byCat.get(it.categoria).push(it);
    });
    const order = [...CATEGORIE, ...[...byCat.keys()].filter((c) => !CATEGORIE.includes(c))];
    return order.filter((c) => byCat.has(c)).map((c) => ({ categoria: c, rows: byCat.get(c) }));
  }, [items]);

  // Groups all "a_persona" items by their headcount reference (a moment, or every
  // confirmed guest) so the couple can see e.g. "Pranzo: 42 persone x (menu +
  // vino + torta) = totale", mirroring how a real catering quote breaks down.
  const perPersonSummary = useMemo(() => {
    const byRef = new Map();
    items.filter((i) => i.costo_tipo === "a_persona").forEach((i) => {
      const ref = i.moment_id || "all";
      if (!byRef.has(ref)) byRef.set(ref, []);
      byRef.get(ref).push(i);
    });
    return [...byRef.entries()].map(([ref, rows]) => {
      const headcount = headcounts[ref] ?? headcounts.all;
      const unitTotal = rows.reduce((s, r) => s + (r.prezzo_unitario || 0), 0);
      return { ref, label: momentLabel(ref), headcount, rows, unitTotal, subtotal: unitTotal * headcount };
    });
  }, [items, headcounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingSuggestions = useMemo(() => {
    const existing = new Set(items.map((i) => `${i.categoria}::${i.nome}`.toLowerCase()));
    return SUGGESTIONS.filter((s) => !existing.has(`${s.categoria}::${s.nome}`.toLowerCase()));
  }, [items]);

  const saveBudgetTotale = async () => {
    const value = budgetInput.trim() === "" ? null : Number(budgetInput);
    if (value != null && (Number.isNaN(value) || value < 0)) return toast.error("Budget non valido");
    try {
      const { data } = await api.put("/owner/wedding", { budget_totale: value });
      setWedding((w) => ({ ...w, budget_totale: data.budget_totale }));
      toast.success("Budget aggiornato");
    } catch {
      toast.error("Errore nel salvataggio del budget");
    }
  };

  const openNew = (prefill = {}) => setModalItem({ ...emptyForm(), ...prefill });
  const openEdit = (item) => setModalItem({
    id: item.id,
    categoria: item.categoria,
    nome: item.nome,
    fornitore: item.fornitore || "",
    costo_tipo: item.costo_tipo === "a_persona" ? "a_persona" : "fisso",
    costo_previsto: item.costo_previsto != null ? String(item.costo_previsto) : "",
    prezzo_unitario: item.prezzo_unitario != null ? String(item.prezzo_unitario) : "",
    moment_ref: item.moment_id || "all",
    costo_effettivo: item.costo_effettivo != null ? String(item.costo_effettivo) : "",
    stato: item.stato,
    scadenza: item.scadenza || "",
    note: item.note || "",
    fatto: !!item.fatto,
  });

  const unitPreviewTotal = (form) => {
    const unit = Number(form.prezzo_unitario) || 0;
    const count = headcounts[form.moment_ref] ?? headcounts.all;
    return unit * count;
  };

  const submitModal = async (e) => {
    e.preventDefault();
    if (!modalItem.nome.trim()) return toast.error("Inserisci un nome per la voce");
    if (modalItem.costo_tipo === "a_persona" && !modalItem.prezzo_unitario) {
      return toast.error("Inserisci il prezzo a persona");
    }
    setSaving(true);
    const isPerPerson = modalItem.costo_tipo === "a_persona";
    const payload = {
      categoria: modalItem.categoria,
      nome: modalItem.nome.trim(),
      fornitore: modalItem.fornitore.trim() || null,
      costo_tipo: modalItem.costo_tipo,
      prezzo_unitario: isPerPerson ? Number(modalItem.prezzo_unitario) : null,
      moment_id: isPerPerson && modalItem.moment_ref !== "all" ? modalItem.moment_ref : null,
      costo_previsto: isPerPerson ? unitPreviewTotal(modalItem) : (modalItem.costo_previsto === "" ? 0 : Number(modalItem.costo_previsto)),
      costo_effettivo: modalItem.costo_effettivo === "" ? 0 : Number(modalItem.costo_effettivo),
      stato: modalItem.stato,
      scadenza: modalItem.scadenza || null,
      note: modalItem.note.trim() || null,
      fatto: modalItem.fatto,
    };
    try {
      if (modalItem.id) {
        const { data } = await api.put(`/owner/budget/${modalItem.id}`, payload);
        setItems((its) => its.map((i) => (i.id === data.id ? data : i)));
      } else {
        const { data } = await api.post("/owner/budget", payload);
        setItems((its) => [...its, data]);
      }
      setModalItem(null);
      toast.success("Salvato");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleFatto = async (item) => {
    const fatto = !item.fatto;
    setItems((its) => its.map((i) => (i.id === item.id ? { ...i, fatto } : i)));
    try {
      await api.put(`/owner/budget/${item.id}`, { fatto });
    } catch {
      setItems((its) => its.map((i) => (i.id === item.id ? { ...i, fatto: !fatto } : i)));
      toast.error("Errore nel salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa voce?")) return;
    try {
      await api.delete(`/owner/budget/${id}`);
      setItems((its) => its.filter((i) => i.id !== id));
      toast.success("Voce eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const addSuggestion = async (s) => {
    try {
      const { data } = await api.post("/owner/budget", { categoria: s.categoria, nome: s.nome });
      setItems((its) => [...its, data]);
    } catch {
      toast.error("Errore nell'aggiunta");
    }
  };

  if (!loaded) return null;

  return (
    <div data-testid="budget-tab">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h2 className="font-wed text-2xl">Budget & Organizzazione</h2>
          <p className="text-sm text-neutral-500 mt-1">Tieni traccia di tutte le spese e di cosa resta da organizzare per il matrimonio.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-outline-wed flex items-center gap-2" onClick={() => setShowPrint(true)} data-testid="budget-print-button">
            <Printer size={16} /> Stampa
          </button>
          <button className="btn-wed flex items-center gap-2" onClick={() => openNew()} data-testid="budget-add-button">
            <Plus size={16} /> Aggiungi voce
          </button>
        </div>
      </div>

      {showPrint && (
        <BudgetPrint wedding={wedding} totals={totals} grouped={grouped} perPersonSummary={perPersonSummary} statiLabels={STATI} fmtMoney={fmtMoney} onClose={() => setShowPrint(false)} />
      )}

      {/* Summary */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#e3ddd2" }} data-testid="budget-card-totale">
          <p className="text-xs text-neutral-400 mb-1">Budget totale</p>
          <div className="flex items-center gap-1">
            <span className="text-neutral-400">€</span>
            <input
              className="input-wed !py-1.5 text-lg font-wed"
              type="number"
              min={0}
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              onBlur={saveBudgetTotale}
              placeholder="non impostato"
              data-testid="budget-totale-input"
            />
          </div>
        </div>
        <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#e3ddd2" }} data-testid="budget-card-previsto">
          <p className="text-xs text-neutral-400 mb-1">Previsto totale</p>
          <p className="text-2xl font-wed">{fmtMoney(totals.previsto)}</p>
        </div>
        <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#e3ddd2" }} data-testid="budget-card-pagato">
          <p className="text-xs text-neutral-400 mb-1">Pagato finora</p>
          <p className="text-2xl font-wed" style={{ color: "var(--wed-primary)" }}>{fmtMoney(totals.pagato)}</p>
        </div>
        <div className="bg-white border rounded-2xl p-4" style={{ borderColor: "#e3ddd2" }} data-testid="budget-card-rimanente">
          <p className="text-xs text-neutral-400 mb-1">Rimanente</p>
          <p className="text-2xl font-wed">{totals.rimanente != null ? fmtMoney(totals.rimanente) : "—"}</p>
        </div>
      </div>

      {totals.pct != null && (
        <div className="mb-8">
          <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${totals.pct}%`, background: "var(--wed-primary)" }} data-testid="budget-progress-bar" />
          </div>
          <p className="text-xs text-neutral-400 mt-1.5">{totals.pct}% del budget totale già speso</p>
        </div>
      )}

      {/* Per-person breakdown */}
      {perPersonSummary.length > 0 && (
        <div className="bg-white border rounded-2xl p-5 mb-6" style={{ borderColor: "#e3ddd2" }} data-testid="budget-per-person-card">
          <h3 className="font-wed text-lg mb-1 flex items-center gap-2"><Users size={17} style={{ color: "var(--wed-primary)" }} /> Riepilogo costi a persona</h3>
          <p className="text-xs text-neutral-400 mb-4">Calcolato sugli invitati confermati, in base ai momenti a cui partecipano (Pagina & Tema → Momenti dell'evento).</p>
          <div className="space-y-3">
            {perPersonSummary.map((g) => (
              <div key={g.ref} className="flex items-center justify-between gap-3 flex-wrap text-sm border-t pt-3 first:border-t-0 first:pt-0" style={{ borderColor: "#f0ece1" }} data-testid={`budget-per-person-${g.ref}`}>
                <div>
                  <p className="font-medium">{g.label} <span className="text-neutral-400 font-normal">· {g.headcount} persone</span></p>
                  <p className="text-xs text-neutral-400">{g.rows.map((r) => `${r.nome} (${fmtMoney(r.prezzo_unitario)})`).join(" + ")}</p>
                </div>
                <p className="font-wed text-lg shrink-0">{fmtMoney(g.subtotal)}</p>
              </div>
            ))}
            <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "#f0ece1" }}>
              <p className="text-sm font-medium">Totale costi a persona</p>
              <p className="font-wed text-xl" style={{ color: "var(--wed-primary)" }}>{fmtMoney(perPersonSummary.reduce((s, g) => s + g.subtotal, 0))}</p>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {pendingSuggestions.length > 0 && (
        <div className="bg-[#faf6ee] border rounded-2xl p-4 mb-6" style={{ borderColor: "#e3ddd2" }} data-testid="budget-suggestions-card">
          <button type="button" className="flex items-center gap-2 text-sm font-medium" onClick={() => setShowSuggestions((s) => !s)} data-testid="budget-suggestions-toggle">
            <Lightbulb size={16} style={{ color: "var(--wed-primary)" }} />
            Suggerimenti: {pendingSuggestions.length} voci comuni non ancora in lista
          </button>
          {showSuggestions && (
            <div className="flex flex-wrap gap-2 mt-3">
              {pendingSuggestions.map((s) => (
                <button
                  key={`${s.categoria}::${s.nome}`}
                  type="button"
                  onClick={() => addSuggestion(s)}
                  className="flex items-center gap-1.5 text-xs bg-white border rounded-full px-3 py-1.5 hover:border-[var(--wed-primary)] transition-colors duration-150"
                  style={{ borderColor: "#e3ddd2" }}
                  data-testid={`budget-suggestion-${s.nome.replace(/\s+/g, "-")}`}
                >
                  <Plus size={12} /> {s.nome}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Items grouped by category */}
      {items.length === 0 ? (
        <p className="text-neutral-400 py-14 text-center border border-dashed rounded-2xl" style={{ borderColor: "#e3ddd2" }} data-testid="budget-empty">
          Nessuna voce ancora. Aggiungi la prima spesa o scegli un suggerimento qui sopra.
        </p>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ categoria, rows }) => {
            const catPrevisto = rows.reduce((s, i) => s + (i.costo_previsto || 0), 0);
            const catPagato = rows.reduce((s, i) => s + (i.costo_effettivo || 0), 0);
            return (
              <div key={categoria} data-testid={`budget-category-${categoria}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-wed text-lg">{categoria}</h3>
                  <p className="text-xs text-neutral-400">{fmtMoney(catPagato)} / {fmtMoney(catPrevisto)}</p>
                </div>
                <div className="bg-white border rounded-2xl divide-y overflow-hidden" style={{ borderColor: "#e3ddd2" }}>
                  {rows.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-3 flex-wrap sm:flex-nowrap" data-testid={`budget-row-${item.id}`}>
                      <button
                        type="button"
                        onClick={() => toggleFatto(item)}
                        className="w-5 h-5 rounded-full border flex items-center justify-center shrink-0"
                        style={{ borderColor: item.fatto ? "var(--wed-primary)" : "#d4cfc2", background: item.fatto ? "var(--wed-primary)" : "white" }}
                        data-testid={`budget-done-toggle-${item.id}`}
                      >
                        {item.fatto && <Check size={13} className="text-white" />}
                      </button>
                      <div className="flex-1 min-w-[160px]">
                        <p className={`text-sm font-medium ${item.fatto ? "line-through text-neutral-400" : ""}`}>{item.nome}</p>
                        {item.fornitore && <p className="text-xs text-neutral-400">{item.fornitore}</p>}
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full text-white shrink-0" style={{ background: STATO_COLORS[item.stato] || "#9a9a9a" }} data-testid={`budget-stato-${item.id}`}>
                        {STATI[item.stato] || item.stato}
                      </span>
                      {item.scadenza && <span className="text-xs text-neutral-400 shrink-0">entro {new Date(item.scadenza).toLocaleDateString("it-IT")}</span>}
                      {item.costo_tipo === "a_persona" && (
                        <span className="text-xs text-neutral-400 shrink-0 flex items-center gap-1">
                          <Users size={11} /> {fmtMoney(item.prezzo_unitario)}/persona · {momentLabel(item.moment_id || "all")}
                        </span>
                      )}
                      <span className="text-sm shrink-0 w-28 text-right">{fmtMoney(item.costo_effettivo)} <span className="text-neutral-400">/ {fmtMoney(item.costo_previsto)}</span></span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openEdit(item)} className="p-1.5 text-neutral-400 hover:text-neutral-700 transition-colors duration-150" data-testid={`budget-edit-${item.id}`}><Pencil size={15} /></button>
                        <button onClick={() => remove(item.id)} className="p-1.5 text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`budget-delete-${item.id}`}><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/edit modal */}
      {modalItem && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setModalItem(null)} data-testid="budget-modal">
          <form onSubmit={submitModal} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#faf9f6] rounded-3xl p-7 space-y-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-wed text-2xl">{modalItem.id ? "Modifica voce" : "Nuova voce"}</h3>
              <button type="button" onClick={() => setModalItem(null)} className="text-neutral-400 hover:text-neutral-700" data-testid="budget-modal-close"><X size={20} /></button>
            </div>

            <div>
              <label className="label-wed">Nome *</label>
              <input className="input-wed" value={modalItem.nome} onChange={(e) => setModalItem({ ...modalItem, nome: e.target.value })} placeholder="Es. Fotografo" data-testid="budget-form-nome" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-wed">Categoria</label>
                <select className="input-wed" value={modalItem.categoria} onChange={(e) => setModalItem({ ...modalItem, categoria: e.target.value })} data-testid="budget-form-categoria">
                  {CATEGORIE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label-wed">Fornitore</label>
                <input className="input-wed" value={modalItem.fornitore} onChange={(e) => setModalItem({ ...modalItem, fornitore: e.target.value })} placeholder="Nome fornitore" data-testid="budget-form-fornitore" />
              </div>
            </div>

            <div className="flex rounded-xl border overflow-hidden text-sm" style={{ borderColor: "#e3ddd2" }} data-testid="budget-form-costo-tipo">
              <button
                type="button"
                onClick={() => setModalItem({ ...modalItem, costo_tipo: "fisso" })}
                className="flex-1 py-2 transition-colors duration-150"
                style={modalItem.costo_tipo === "fisso" ? { background: "var(--wed-primary)", color: "white" } : { background: "white", color: "#525252" }}
              >
                Costo fisso
              </button>
              <button
                type="button"
                onClick={() => setModalItem({ ...modalItem, costo_tipo: "a_persona" })}
                className="flex-1 py-2 flex items-center justify-center gap-1.5 transition-colors duration-150"
                style={modalItem.costo_tipo === "a_persona" ? { background: "var(--wed-primary)", color: "white" } : { background: "white", color: "#525252" }}
              >
                <Users size={14} /> A persona
              </button>
            </div>

            {modalItem.costo_tipo === "a_persona" ? (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label-wed">Prezzo a persona (€)</label>
                    <input className="input-wed" type="number" min={0} value={modalItem.prezzo_unitario} onChange={(e) => setModalItem({ ...modalItem, prezzo_unitario: e.target.value })} data-testid="budget-form-prezzo-unitario" />
                  </div>
                  <div>
                    <label className="label-wed">Riferimento persone</label>
                    <select className="input-wed" value={modalItem.moment_ref} onChange={(e) => setModalItem({ ...modalItem, moment_ref: e.target.value })} data-testid="budget-form-moment-ref">
                      <option value="all">Tutti i confermati ({headcounts.all})</option>
                      {moments.map((m) => (
                        <option key={m.id} value={m.id}>{m.label} ({headcounts[m.id] ?? 0})</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-sm bg-[#faf6ee] rounded-xl px-3 py-2" data-testid="budget-form-preview">
                  {fmtMoney(Number(modalItem.prezzo_unitario) || 0)} × {headcounts[modalItem.moment_ref] ?? headcounts.all} persone = <strong>{fmtMoney(unitPreviewTotal(modalItem))}</strong>
                </p>
              </div>
            ) : (
              <div>
                <label className="label-wed">Costo previsto (€)</label>
                <input className="input-wed" type="number" min={0} value={modalItem.costo_previsto} onChange={(e) => setModalItem({ ...modalItem, costo_previsto: e.target.value })} data-testid="budget-form-previsto" />
              </div>
            )}

            <div>
              <label className="label-wed">Pagato finora (€)</label>
              <input className="input-wed" type="number" min={0} value={modalItem.costo_effettivo} onChange={(e) => setModalItem({ ...modalItem, costo_effettivo: e.target.value })} data-testid="budget-form-effettivo" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-wed">Stato</label>
                <select className="input-wed" value={modalItem.stato} onChange={(e) => setModalItem({ ...modalItem, stato: e.target.value })} data-testid="budget-form-stato">
                  {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="label-wed">Scadenza</label>
                <input className="input-wed" type="date" value={modalItem.scadenza} onChange={(e) => setModalItem({ ...modalItem, scadenza: e.target.value })} data-testid="budget-form-scadenza" />
              </div>
            </div>

            <div>
              <label className="label-wed">Note</label>
              <textarea className="input-wed" rows={3} value={modalItem.note} onChange={(e) => setModalItem({ ...modalItem, note: e.target.value })} data-testid="budget-form-note" />
            </div>

            <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
              <input type="checkbox" checked={modalItem.fatto} onChange={(e) => setModalItem({ ...modalItem, fatto: e.target.checked })} data-testid="budget-form-fatto" />
              Completato / risolto
            </label>

            <button type="submit" className="btn-wed w-full" disabled={saving} data-testid="budget-form-submit">
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
